import cx from 'classnames';
import { add, parseISO } from 'date-fns';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Appointment } from '../features/serviceLocation/serviceLocationSlice';
import styles from '../sass/components/TimeList.module.scss';
import {
  formatCents,
  formatSignedCents,
  slotAdjustmentCents,
  slotTierLabel,
  SlotPriceTier,
} from '../util/bookingPricing';
import { formatDate, formatTime } from '../i18n/format';
import { zoneSuffix } from '../util/slotTime';

interface TimeListProps {
  times?: Appointment[];
  onSelectAppointment: (appt: Appointment) => void;
  selectedTime?: Appointment | null;
  /** Show airline-style prices next to each time. */
  showPrices?: boolean;
  /** Services subtotal (cents) the slot percentage is applied to. */
  eligibleSubtotalCents?: number;
  /** Pre-tax order subtotal (cents, fees included) prices are shown against. */
  displaySubtotalCents?: number;
}

export default function TimeList({
  times,
  onSelectAppointment,
  selectedTime,
  showPrices = false,
  eligibleSubtotalCents = 0,
  displaySubtotalCents = eligibleSubtotalCents,
}: TimeListProps) {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    wrapperRef.current?.scrollTo({ top: 0 });
  }, [times]);

  const selectedDay = (times && times.length)
    ? new Date(times[0].date)
    : new Date();

  // Enter or Space picks the time. It used to be any key at all, so Tab
  // (moving on) picked the focused time and closed the picker.
  const handleKeyDown = (event: React.KeyboardEvent, appt: Appointment) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onSelectAppointment(appt);
  };

  const isSelected = (time: Appointment) => selectedTime?.date === time.date
    && selectedTime.date === time.date
    && selectedTime.routeId === time.routeId;

  const renderPrice = (time: Appointment) => {
    if (!showPrices) return null;
    const adjustment = slotAdjustmentCents(time, eligibleSubtotalCents);
    const label = slotTierLabel(time);
    return (
      <span className={styles.priceColumn}>
        <span className={styles.price}>
          {formatCents(displaySubtotalCents + adjustment)}
        </span>
        {label && (
          <span
            className={cx(styles.tierTag, {
              [styles.lowest]: time.isLowestPrice,
              [styles.peak]: !time.isLowestPrice
                && time.priceTier === SlotPriceTier.peak,
              [styles.discount]: !time.isLowestPrice
                && time.priceTier === SlotPriceTier.discount,
            })}
          >
            {label}
            {adjustment !== 0 && ` (${formatSignedCents(adjustment)})`}
          </span>
        )}
      </span>
    );
  };

  return (
    <div
      ref={wrapperRef}
      className={cx(styles.wrapper, { [styles.wide]: showPrices })}
    >
      <div key={selectedDay.toISOString()} className={styles.daySection}>
        <div className={styles.daySectionHeader}>
          <span className="label">
            {formatDate(selectedDay, 'EEEE, MMMM do, yyyy', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        </div>
        {/* The options' listbox (axe aria-required-parent, QA #30). */}
        <div
          className={styles.appointmentTimes}
          role="listbox"
          aria-label={t('schedule.times')}
        >
          {times?.map((time) => (
            <div
              key={`${selectedDay}-${time.routeId}-${time.date}`}
              className={cx(styles.time, {
                [styles.selected]: isSelected(time),
                [styles.booked]: time.isBooked,
                [styles.withPrice]: showPrices,
              })}
              tabIndex={0}
              role="option"
              aria-selected={isSelected(time)}
              onClick={() => onSelectAppointment(time)}
              onKeyDown={(event) => handleKeyDown(event, time)}
            >
              <span>
                {formatTime(parseISO(time.date), 'hh:mmaaa')}
                {' '}
                -
                {' '}
                {formatTime(add(parseISO(time.date), { hours: 1 }), 'hh:mmaaa')}
                {zoneSuffix(time)}
              </span>
              {renderPrice(time)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
